import * as React from "react";
import { AlertDialog, Button, Flex } from "@radix-ui/themes";
import { useConvex, useMutation } from "convex/react";
import { api } from "@repo/convex/convex/_generated/api";
import { Id } from "@repo/convex/convex/_generated/dataModel";
import { toast } from "sonner";
import { FunctionReturnType } from "convex/server";

interface Props {
  count: number;
}

/**
 * Deletes everything in the trash now rather than in 30 days. There's no mutation for the lot, so
 * it lists the trash a page at a time and deletes each with `items:permanentlyDelete`, the same as
 * the per-item "Delete forever".
 */
export const EmptyTrashButton: React.FC<Props> = ({ count }) => {
  const convex = useConvex();
  const permanentlyDelete = useMutation(api.items.permanentlyDelete);
  const [isEmptying, setIsEmptying] = React.useState(false);

  const emptyTrash = async () => {
    setIsEmptying(true);
    try {
      const ids: Id<"items">[] = [];
      let cursor: string | null = null;
      for (;;) {
        const page: FunctionReturnType<typeof api.items.list> = await convex.query(api.items.list, {
          statusKind: "trashed",
          paginationOpts: { numItems: 100, cursor },
        });
        ids.push(...page.page.filter((item) => item.status.kind == "trashed").map((i) => i._id));
        if (page.isDone) break;
        cursor = page.continueCursor;
      }
      for (const itemId of ids) await permanentlyDelete({ itemId });
      toast(ids.length == 1 ? "Deleted 1 thing for good" : `Deleted ${ids.length} things for good`);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't empty the trash. Some of it may still be there.");
    } finally {
      setIsEmptying(false);
    }
  };

  return (
    <AlertDialog.Root>
      <AlertDialog.Trigger>
        <Button variant="soft" color="red" loading={isEmptying}>
          Empty trash
        </Button>
      </AlertDialog.Trigger>
      <AlertDialog.Content maxWidth="420px">
        <AlertDialog.Title>Empty the trash?</AlertDialog.Title>
        <AlertDialog.Description size="2">
          {count == 1
            ? "The 1 thing in it goes for good, with its notes and saved article."
            : `All ${count >= 500 ? "500+" : count} things in it go for good, with their notes and saved articles.`}{" "}
          There's no undo for this one.
        </AlertDialog.Description>
        <Flex gap="3" mt="4" justify="end">
          <AlertDialog.Cancel>
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button color="red" onClick={() => void emptyTrash()}>
              Empty trash
            </Button>
          </AlertDialog.Action>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
};
